/* =========================================================
   Disassociation requests.

   Authorising a disassociation (executing or dismissing it) is
   exclusive to the ETC Admin. Partner Managers and Site
   Managers see the same list in read-only mode with a
   "Pendiente de Admin ETC" lock chip in place of the actions.
   ========================================================= */
import { useEffect, useReducer, useState } from 'react';
import type { DisassocRequest, Instructor } from '@/types';
import { ic, sized } from '@/components/Icons';
import { DataTable, Modal, PageHeader, Pagination, type Column } from '@/components/ui';
import { ds } from '@/data';
import { useI18n } from '@/i18n';
import { paginate, sortRows, useTableState, type SortMap } from '@/hooks/useTableState';
import { useGo } from '@/hooks/useRoute';
import { useAppStore } from '@/store/useAppStore';
import { InstrAvatar } from './InstrAvatar';

type Row = DisassocRequest & { _key: string; _instr: Partial<Instructor>; _name: string };

export function DisassocRequests() {
  const { lang, t } = useI18n();
  const go = useGo();
  const role = useAppStore((s) => s.role);
  const addToast = useAppStore((s) => s.addToast);
  const canAuthorize = role === 'ETC_ADMIN';

  const [, force] = useReducer((x: number) => x + 1, 0);
  const [confirm, setConfirm] = useState<Row | null>(null);
  const [rejecting, setRejecting] = useState<Row | null>(null);
  const [reason, setReason] = useState('');
  const table = useTableState(10);

  // Las bandejas llegan con la hidratación, que ocurre solo al iniciar
  // sesión. Sin esto, una solicitud que un socio acaba de enviar no le
  // aparece al Admin ETC hasta que vuelve a entrar.
  useEffect(() => {
    void ds.refreshRequests().then(force).catch(() => {
      /* si falla se sigue viendo lo que ya había; no vale la pena un aviso */
    });
  }, []);

  const reqs = ds.listDisassocRequests();
  const enriched: Row[] = reqs.map((r) => {
    const i: Partial<Instructor> = ds.getInstructor(r.InstructorId) ?? {};
    return {
      ...r,
      _key: r.InstructorId + '|' + r.SiteId,
      _instr: i,
      _name: `${i.EnglishFirstName || '?'} ${i.EnglishLastName || ''}`.trim(),
    };
  });

  const sortValues: SortMap<Row> = {
    name: { type: 'text', get: (r) => `${r._instr.EnglishLastName || ''} ${r._instr.EnglishFirstName || ''}`.trim() },
    site: { type: 'text', get: (r) => r.SiteName || '' },
    org: { type: 'text', get: (r) => r.OrgName || '' },
    date: { type: 'text', get: (r) => r.date || '' },
  };

  const { rows, page, pageCount } = paginate(sortRows(enriched, table.sort, sortValues), table.page, table.per);

  function execute(r: Row) {
    // un solo paso: retirar la afiliación y cerrar la solicitud van juntas
    const left = ds.authorizeDisassociation(r.InstructorId, r.SiteId);
    addToast({
      kind: 'delete',
      title: lang === 'es' ? 'Instructor desasociado del site' : 'Instructor disassociated from site',
      body:
        `${r._name} · ${r.SiteName}` +
        (left === 0
          ? (lang === 'es' ? ' · Quedó sin afiliaciones (desasociado)' : ' · No affiliations left (disassociated)')
          // Callarse cuando quedan afiliaciones hacía buscar al instructor en
          // «Instructores desasociados», donde no está ni tiene por qué estar
          // (prueba 5.6). Decir cuántas le quedan cierra la pregunta.
          : lang === 'es'
            ? ` · Le ${left === 1 ? 'queda 1 afiliación' : `quedan ${left} afiliaciones`}: sigue activo`
            : ` · ${left} affiliation${left === 1 ? '' : 's'} left: still active`),
      origin: lang === 'es' ? 'Instructores · Solicitudes de desasociación' : 'Instructors · Disassociation requests',
      details:
        `${r._name} (${r.InstructorId}) — ${r.SiteName} (${r.SiteId}) · ${r.OrgName}`
        + (left === 0
          ? (lang === 'es'
              ? '\nYa puedes recolocarlo desde Administración → Instructores desasociados.'
              : '\nYou can reassign them from Administration → Disassociated instructors.')
          : (lang === 'es'
              ? '\nNo aparecerá en «Instructores desasociados»: esa lista es solo para quienes se quedaron sin ninguna afiliación.'
              : '\nThey will not appear under “Disassociated instructors”: that list only holds those with no affiliation left.')),
    });
    setConfirm(null);
    force();
  }

  // Rechazar lleva motivo, igual que en el cambio de contacto legal: el
  // socio recibe el aviso con la razón, y así no vuelve a pedir lo mismo.
  function dismiss() {
    const r = rejecting;
    if (!r || !reason.trim()) return;
    ds.rejectDisassociation(r.InstructorId, r.SiteId, reason.trim());
    addToast({
      kind: 'ok',
      title: lang === 'es' ? 'Solicitud rechazada' : 'Request dismissed',
      body: `${r._name} · ${r.SiteName}`,
      details: lang === 'es' ? 'Se le avisó al socio con el motivo.' : 'The partner was notified with the reason.',
    });
    setRejecting(null);
    setReason('');
    force();
  }

  const columns: Column<Row>[] = [
    {
      key: 'name',
      label: 'Instructor',
      sortable: true,
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <InstrAvatar instr={r._instr} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{r._name}</div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.InstructorId}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'site',
      label: 'Site',
      sortable: true,
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.SiteName}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.SiteId}</div>
        </div>
      ),
    },
    {
      key: 'org',
      label: lang === 'es' ? 'Organización' : 'Organization',
      muted: true,
      sortable: true,
      render: (r) => (
        <div>
          <div>{r.OrgName}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.OrgId}</div>
        </div>
      ),
    },
    {
      key: 'reason',
      label: lang === 'es' ? 'Motivo' : 'Reason',
      // Es lo que hay que leer para decidir. Cuando falta se dice, en vez
      // de dejar un hueco que parezca un fallo de carga.
      render: (r) => (
        r.reason
          ? <div style={{ fontSize: 12, maxWidth: 320, lineHeight: 1.45 }}>{r.reason}</div>
          : <span style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic' }}>
              {lang === 'es' ? 'sin motivo' : 'no reason given'}
            </span>
      ),
    },
    { key: 'date', label: lang === 'es' ? 'Fecha solicitud' : 'Request date', mono: true, sortable: true },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) => (
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <button
            className="btn sm"
            onClick={(e) => {
              e.stopPropagation();
              go('instructorDetail', { instructorId: r.InstructorId });
            }}
          >
            {t('common.view')}
          </button>
          {canAuthorize ? (
            <>
              <button className="btn sm" onClick={(e) => { e.stopPropagation(); setRejecting(r); setReason(''); }}>
                {ic.close} {lang === 'es' ? 'Rechazar' : 'Dismiss'}
              </button>
              <button className="btn sm danger" onClick={(e) => { e.stopPropagation(); setConfirm(r); }}>
                {ic.trash} {lang === 'es' ? 'Desasociar' : 'Disassociate'}
              </button>
            </>
          ) : (
            <span
              className="chip"
              data-tip={lang === 'es' ? 'Autorización exclusiva del Admin ETC' : 'Authorization restricted to ETC Admin'}
              style={{ fontSize: 10.5 }}
            >
              {sized(ic.lock, 11)} {lang === 'es' ? 'Pendiente de Admin ETC' : 'Pending ETC Admin'}
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={lang === 'es' ? 'Solicitudes de desasociación' : 'Disassociation requests'}
        sub={lang === 'es' ? `${reqs.length} solicitud(es) pendiente(s) de procesar` : `${reqs.length} pending request(s)`}
        crumbs={[
          { label: t('nav.instructors'), onClick: () => go('instructors') },
          { label: lang === 'es' ? 'Solicitudes de desasociación' : 'Disassociation requests' },
        ]}
      />

      {!canAuthorize ? (
        <div className="card" style={{ padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center', background: 'var(--bg-2)', border: '1px solid var(--line)' }}>
          <span style={{ color: 'var(--info)', display: 'inline-flex', flexShrink: 0 }}>{sized(ic.info, 16)}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.45 }}>
            {lang === 'es'
              ? 'Solo el Administrador ETC puede autorizar o rechazar desasociaciones. Esta vista es de consulta.'
              : 'Only the ETC Administrator can authorize or dismiss disassociations. This view is read-only.'}
          </span>
        </div>
      ) : null}

      {reqs.length === 0 ? (
        <div className="card" style={{ padding: '34px 16px', textAlign: 'center', color: 'var(--text-3)' }}>
          <div style={{ display: 'inline-flex', marginBottom: 8, color: 'var(--line-2)' }}>{sized(ic.check, 28)}</div>
          <div style={{ fontSize: 13 }}>
            {lang === 'es' ? 'No hay solicitudes de desasociación pendientes.' : 'No pending disassociation requests.'}
          </div>
        </div>
      ) : (
        <>
          <DataTable columns={columns} rows={rows} sort={table.sort} onSort={table.onSort} stickyHeader />
          <Pagination page={page} pageCount={pageCount} onPage={table.setPage} total={reqs.length} per={table.per} onPer={table.onPer} perOptions={[5, 10, 25, 50]} />
        </>
      )}

      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title={lang === 'es' ? 'Rechazar la solicitud' : 'Dismiss the request'}
        footer={
          <>
            <button className="btn" onClick={() => setRejecting(null)}>{t('common.cancel')}</button>
            <button className="btn danger" disabled={!reason.trim()} onClick={dismiss}>
              {ic.close} {lang === 'es' ? 'Rechazar' : 'Dismiss'}
            </button>
          </>
        }
      >
        {rejecting ? (
          <>
            <div style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.5 }}>
              {lang === 'es' ? (
                <>Se le negará a <strong>{rejecting.OrgName}</strong> la desasociación de{' '}
                <strong>{rejecting._name}</strong> del site <strong>{rejecting.SiteName}</strong>.</>
              ) : (
                <>You are denying <strong>{rejecting.OrgName}</strong> the removal of{' '}
                <strong>{rejecting._name}</strong> from <strong>{rejecting.SiteName}</strong>.</>
              )}
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 14 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {lang === 'es' ? 'Motivo' : 'Reason'} <span style={{ color: 'var(--err)' }}>*</span>
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder={lang === 'es'
                  ? 'El instructor sigue impartiendo cursos activos en ese site.'
                  : 'The instructor still teaches active courses at that site.'}
                style={{ padding: '9px 11px', border: '1px solid var(--line-2)', borderRadius: 7, fontSize: 13, background: 'var(--surface)', resize: 'vertical', fontFamily: 'inherit' }}
              />
            </label>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 8, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--info)', flexShrink: 0, display: 'inline-flex' }}>{sized(ic.info, 13)}</span>
              <span>
                {lang === 'es'
                  ? 'El socio recibe este motivo en su campana. Sin él, volvería a pedir lo mismo.'
                  : 'The partner receives this reason in their bell. Without it they would simply ask again.'}
              </span>
            </div>
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={lang === 'es' ? 'Confirmar desasociación' : 'Confirm disassociation'}
        footer={
          <>
            <button className="btn" onClick={() => setConfirm(null)}>{t('common.cancel')}</button>
            <button className="btn danger" onClick={() => confirm && execute(confirm)}>
              {ic.trash} {lang === 'es' ? 'Sí, desasociar' : 'Yes, disassociate'}
            </button>
          </>
        }
      >
        {confirm ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--err)', flexShrink: 0, marginTop: 2 }}>{sized(ic.warn, 22)}</span>
            <div style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.5 }}>
              {lang === 'es' ? (
                <>
                  ¿Seguro que deseas desasociar a <strong>{confirm._name}</strong> (<span className="mono">{confirm.InstructorId}</span>) del site{' '}
                  <strong>{confirm.SiteName}</strong>? Si es su única afiliación, quedará como instructor desasociado.
                </>
              ) : (
                <>
                  Are you sure you want to disassociate <strong>{confirm._name}</strong> (<span className="mono">{confirm.InstructorId}</span>) from{' '}
                  <strong>{confirm.SiteName}</strong>? If it is their only affiliation, they will become a disassociated instructor.
                </>
              )}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
